import { tmxApiCall } from './index'

// Utilities
import Logger from '../utility/loggerUtil'

export default {
  /**
   * Gets all debit cards associated with the customer and their processor types and any card validations.
   * @param {Number} paymentProcessorTypeEnum - The type of payment processor used by the customers loans.
   * @returns {Promise<{cards: [], validations: {}}>} An array of cards and object of validation properties.
   */
  async getDebitCards (paymentProcessorTypeEnum) {
    Logger.info('Started Get Card query.')
    return tmxApiCall('get', '/cards', {
      params: {
        payProviderType: paymentProcessorTypeEnum
      }
    })
  },

  /**
   * Adds a debit card to a customers list of debit cards saved with a payment processor.
   * @param {String} cardNumber - The card number.
   * @param {String} expirationMonth - The card expiration month.
   * @param {String} expirationYear - The card expiration year.
   * @param {Boolean} guestCard - Flag if the card to add is a guest card.
   * @returns {Promise<{card: {cardIds: String, cardToken: String, cardNumber: String, name: String, customerId: String, customerIds: [], guestCard: Boolean}}>} A debit card object.
   */
  async addDebitCard (cardNumber, expirationMonth, expirationYear, guestCard) {
    Logger.info('Started Add Card query.')
    return tmxApiCall('post', '/cards', {
      cardNumber,
      expirationMonth,
      expirationYear,
      guestCard
    })
  },

  addRepayDebitCard (cardHolderName, token, cardNumber, cardType, expirationMonth, expirationYear, guestCard, cardBin) {
    Logger.info('Started Add Repay Card query.')
    return tmxApiCall('post', '/cards', {
      cardNumber,
      expirationMonth,
      expirationYear,
      guestCard,
      repayToken: token,
      cardType,
      cardHolderName,
      cardBin
    })
  },

  /**
   * Adds a debit card, via existing token, to a customers list of debit cards saved with a payment processor.
   * @param {String} tokenPayTempToken - The BridgePay tokenized card.
   * @param {Boolean} guestCard - Flag if the card to add is a guest card.
   * @returns {Promise<{card: {cardIds: String, cardToken: String, cardNumber: String, name: String, customerId: String, customerIds: [], guestCard: Boolean}}>} A debit card object.
   */
  async addTokenizedDebitCard (tokenPayTempToken, guestCard) {
    Logger.info('Started Add Tokenized Card query.')
    return tmxApiCall('post', '/cards', {
      guestCard,
      tokenPayTempToken
    })
  },

  /**
   * Updates a debit card. Currently only supports removing a guest card flag.
   * @param {Object} cardIds - The various LMS card IDs for the card.
   * @param {Boolean} isGuestCard - If false, removes the guest card flag for this card passed in.
   */
  async updateDebitCard (cardIds, isGuestCard = false) {
    Logger.info('Started update card query')
    return tmxApiCall('put', '/cards', {
      cardIds,
      isGuestCard
    })
  },

  async removeDebitCard (cardIds) {
    Logger.info('Started remove card query')
    return tmxApiCall('delete', '/cards', {
      data: {
        cardIds
      }
    })
  },

  /**
   * Creates a new card form key session for the client.
   * @param {String} transactionType - The type of transaction that the card will be used for (payment or debit).
   * @param {String} [storeId] - Optional for referral disbursements, the id of the store.
   * @param {String} [origin] - Optional, include the origin if in a browser.
   * @returns {Promise<{acceptance: Object, acceptanceConfig: {formUrl: String}, key: Object}>} A form key data.
   */
  async getCardFormKey (transactionType, storeId = undefined, origin = undefined) {
    Logger.info('Getting card form key')
    return tmxApiCall('get', '/cardFormKey', {
      params: {
        cardTransactionType: transactionType,
        origin,
        storeId
      }
    })
  },

  /**
   * Checks card details to determine if the card is allowed to be added to the customer's account.
   * @param {String} cardBin - The first 8 digits of the card number.
   * @returns {Promise<{cardDetails: {cardAllowed: Boolean}}>} Whether the card is allowed to be added or not.
   */
  async cardLookup (cardBin) {
    Logger.info('Started Card Lookup query')

    return tmxApiCall('post', '/cardLookup', {
      cardBin: cardBin.substring(0, 8)
    })
  }
}
